import { ReactElement, useState } from 'react';

import { Timestamp } from 'firebase/firestore';

import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

import useOrg from '../../dataHooks/useOrg';
import { useOrgVanityUrlRequestMutators } from '../../dataMutators/useOrgVanityUrlRequestMutators';
import { OrgVanityUrlRequest, ORG_VANITY_URL_REQUEST_STATUSES } from '../../types/Page';
import { DisplayTime } from '../../utils/utils';
import GratiDirectoryItem from '../common/GratiDirectoryItem';
import Hourglass from '../common/Hourglass';

interface OrgVanityUrlRequestListItemProps {
  request: OrgVanityUrlRequest;
  isDividerDisplayed?: boolean;
}

export default function OrgVanityUrlRequestListItem({
  request,
  isDividerDisplayed = true,
}: OrgVanityUrlRequestListItemProps): ReactElement {
  const { org } = useOrg(request.orgId);
  const { approveVanityUrl, rejectVanityUrl } = useOrgVanityUrlRequestMutators();

  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [actionError, setActionError] = useState('');

  const createTime = request.createTime instanceof Timestamp ? request.createTime : Timestamp.now();
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const requestDate = createTime.toDate();
  const monthText = requestDate.toLocaleString('default', { month: 'short', timeZone: tz });
  const dayText = requestDate.toLocaleString('default', { day: 'numeric', timeZone: tz });
  const timeString = DisplayTime(createTime, tz, 'h:mm a');

  const handleApprove = async () => {
    if (!org) return;
    setIsBusy(true);
    setActionError('');
    try {
      await approveVanityUrl(request, org);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setIsBusy(false);
    }
  };

  const handleReject = async () => {
    setIsBusy(true);
    setActionError('');
    try {
      await rejectVanityUrl(request, rejectReason);
      setIsRejectOpen(false);
      setRejectReason('');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to reject');
    } finally {
      setIsBusy(false);
    }
  };

  const getStatusIcon = () => {
    switch (request.status) {
      case ORG_VANITY_URL_REQUEST_STATUSES.PENDING:
        return <HourglassEmptyIcon />;
      case ORG_VANITY_URL_REQUEST_STATUSES.APPROVED:
        return <CheckCircleIcon />;
      case ORG_VANITY_URL_REQUEST_STATUSES.REJECTED:
        return <CancelIcon />;
      case ORG_VANITY_URL_REQUEST_STATUSES.CANCELLED:
        return <DoNotDisturbIcon />;
    }
  };

  const getStatusColor = () => {
    switch (request.status) {
      case ORG_VANITY_URL_REQUEST_STATUSES.PENDING:
        return 'warning.main';
      case ORG_VANITY_URL_REQUEST_STATUSES.APPROVED:
        return 'success.main';
      case ORG_VANITY_URL_REQUEST_STATUSES.REJECTED:
        return 'error.main';
      case ORG_VANITY_URL_REQUEST_STATUSES.CANCELLED:
        return 'text.disabled';
    }
  };

  const StyledDate = (): ReactElement => (
    <Avatar
      variant="rounded"
      sx={{
        bgcolor: 'transparent',
        color: 'text.primary',
        p: 0.5,
        height: '4rem',
        width: '4rem',
      }}
    >
      <Stack direction="column" sx={{
        alignItems: "center"
      }}>
        <Typography variant="titleMedium" sx={{ py: 0, my: 0, lineHeight: 1 }}>
          {monthText} {dayText}
        </Typography>
        <Typography variant="labelMedium" sx={{ pt: 1, my: 0, lineHeight: 1 }}>
          {timeString}
        </Typography>
      </Stack>
    </Avatar>
  );

  const StyledStatus = (): ReactElement => (
    <Tooltip title={request.status}>
      <Avatar sx={{ bgcolor: getStatusColor() }}>{getStatusIcon()}</Avatar>
    </Tooltip>
  );

  const isPending = request.status === ORG_VANITY_URL_REQUEST_STATUSES.PENDING;

  const actionArea = !isPending ? undefined : isBusy ? (
    <Stack direction="row" spacing={1} sx={{
      alignItems: "center"
    }}>
      <Hourglass variant="inline" />
      <Typography variant="bodyMedium" sx={{
        color: "text.secondary"
      }}>
        {isRejectOpen ? 'Rejecting...' : 'Approving...'}
      </Typography>
    </Stack>
  ) : isRejectOpen ? (
    <Stack direction="row" spacing={1} sx={{
      alignItems: "flex-start"
    }}>
      <TextField
        size="small"
        label="Rejection reason"
        value={rejectReason}
        onChange={(e) => setRejectReason(e.target.value)}
        sx={{ width: '14rem' }}
      />
      <Button
        size="small"
        color="error"
        variant="contained"
        disabled={!rejectReason.trim()}
        onClick={handleReject}
      >
        Confirm Rejection
      </Button>
      <Button
        size="small"
        onClick={() => {
          setIsRejectOpen(false);
          setRejectReason('');
        }}
      >
        Cancel
      </Button>
    </Stack>
  ) : (
    <Stack direction="row" spacing={1}>
      <Button
        size="small"
        variant="contained"
        color="success"
        disabled={!org}
        onClick={handleApprove}
      >
        Approve
      </Button>
      <Button
        size="small"
        variant="outlined"
        color="error"
        onClick={() => setIsRejectOpen(true)}
      >
        Reject
      </Button>
    </Stack>
  );

  return (
    <>
      <GratiDirectoryItem
        divider={isDividerDisplayed && !isPending}
        isActionOpen={isRejectOpen}
        slots={{
          icon: <StyledStatus />,
          avatar1: <StyledDate />,
          primary: `${request.orgName} → /pages/${request.requestedVanityUrl}`,
          secondary: `Org: ${request.orgId} · By: ${request.creatorId}`,
          action: actionArea,
        }}
        slotProps={{
          icon: { hideBreakpoint: 'xs' },
        }}
      />
      {actionError && (
        <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
          {actionError}
        </Alert>
      )}
    </>
  );
}
